import React from 'react'
import { SimpleGrid, Center, VStack, Text, Spinner, Box, Icon } from '@chakra-ui/react'
import { AnimatePresence } from 'framer-motion'
import { FiLayers } from 'react-icons/fi'
import { Plan } from '@/hooks/usePlans'
import PlanCard from './PlanCard'
import { MotionBox } from '@/components/Common/MotionBox'

interface PlansGridProps {
  plans: Plan[]
  loading: boolean
  onEdit: (plan: Plan) => void
  onDelete: (plan: Plan) => void
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      staggerChildren: 0.1,
    },
  },
}

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
}

export default function PlansGrid({ plans, loading, onEdit, onDelete }: PlansGridProps) {
  if (loading) {
    return (
      <Center py={16}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="3px" />
          <Text color="gray.600" fontSize="lg">
            Loading plans...
          </Text>
        </VStack>
      </Center>
    )
  }

  if (plans.length === 0) {
    return (
      <Center py={16}>
        <VStack spacing={6} textAlign="center" maxW="md">
          <Box color="gray.400" fontSize="4xl">
            <Icon as={FiLayers} boxSize={16} />
          </Box>
          <VStack spacing={2}>
            <Text fontSize="xl" fontWeight="semibold" color="gray.600">
              No plans found
            </Text>
            <Text fontSize="md" color="gray.500">
              Create your first plan to get started with organization management.
            </Text>
          </VStack>
        </VStack>
      </Center>
    )
  }

  return (
    <MotionBox initial="hidden" animate="visible" variants={containerVariants}>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} w="full">
        <AnimatePresence>
          {plans.map((plan) => (
            <MotionBox
              key={plan.id}
              variants={cardVariants}
              layout
              initial="hidden"
              animate="visible"
              exit="hidden"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
            >
              <PlanCard plan={plan} onEdit={onEdit} onDelete={onDelete} />
            </MotionBox>
          ))}
        </AnimatePresence>
      </SimpleGrid>
    </MotionBox>
  )
}
